/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 * 
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 * 
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file sets up the logic for retrieving an individual patient.
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * Used to create logic based side effects for the redux store.
 */
import { createLogic } from 'redux-logic';

/**
 * Typings
 */
import { TypedUseSelectorHook, useSelector } from 'react-redux';

/**
 * Utility library for reducing redux boilerplate while remaining typed. 
 */
import { ImmerReducer, createActionCreators, createReducerFunction } from 'immer-reducer';

/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */

/*
 * Used to register the logics so that they are included in the store. 
 */
import reducerRegistry from '../ReducerRegistry';

/*
 * Used to get access to the API types and requests
 */
import * as Dtos from '../../api/dtos';

/**
 * Enumeration used to determine the state of a request.
 */
import { IRequestState, RequestState } from '.';

/**
 * Used to create a get request to the servicestack API.
 */
import { createGetRequest } from '../../utilities/createRequest';

/**
 * Typings
 */
import { ILogicSideEffect } from '../../utilities/logicTypes';
import client from '../../api/client';
import { Dispatch } from 'redux';
import { JournalEntryLine } from '../../api/dtos';

/*
* ---------------------------------------------------------------------------------
* Interfaces / Types
* ---------------------------------------------------------------------------------
*/
export type TransactionType = 'bills' | 'expenses' | 'salesReceipts' | 'deposits' | 'journalEntries';
export type AttachmentAndMeta = {
    fileIndex: number;
    type: TransactionType;
    typeIndex: number;
    attachmentIndex: number;
    attachment: IUploadedAttachment;
}

export enum IifFileState {
    load = 1,
    loading = 2,
    refresh = 3,
    loaded = 4,
    remove = 5
}

export interface IifFileInformation {
    state: IifFileState;
    file: File;
}

export interface IIifFile extends Omit<Dtos.ImportFile, 'bills' | 'expenses' | 'salesReceipts' | 'billAttachments' | 'expenseAttachments' | 'salesReceiptAttachments' | 'deposits' | 'depositAttachments' | 'journalEntries' | 'journalEntryAttachments'> {
    billView: number[];
    billErrors: boolean;
    expenseView: number[];
    expenseErrors: boolean;
    salesReceiptView: number[];
    depositView: number[];
    salesReceiptErrors: boolean;
    depositErrors: boolean;
    journalEntryView: number[];
    journalEntryErrors: boolean;
    attachmentsDownloaded: boolean;
    attachmentsImported: boolean;
    attachmentErrors: boolean;
    financialYearErrors: number;
}

export interface IUploadedJournalEntry extends Omit<Dtos.JournalEntry, 'lines'> {
    lineView: number[];
    attachmentView: number[];
    lineErrors: boolean;
    amountError: boolean;
    currencyError: boolean;
    attachmentsDownloaded: boolean;
    attachmentsImported: boolean;
    attachmentErrors: boolean;
    financialYearError: boolean;
}

export interface IUploadedBill extends Omit<Dtos.Bill, 'distributions'> {
    distributionView: number[];
    attachmentView: number[];
    distributionErrors: boolean;
    vendorError: boolean;
    accountError: boolean;
    amountError: boolean;
    currencyError: boolean;
    attachmentsDownloaded: boolean;
    attachmentsImported: boolean;
    attachmentErrors: boolean;
    financialYearError: boolean;
}

export interface IUploadedExpense extends Omit<Dtos.Expense, 'distributions'> {
    distributionView: number[];
    attachmentView: number[];
    distributionErrors: boolean;
    vendorError: boolean;
    accountError: boolean;
    amountError: boolean;
    currencyError: boolean;
    paymentMethodError: boolean;
    attachmentsDownloaded: boolean;
    attachmentsImported: boolean;
    attachmentErrors: boolean;
    financialYearError: boolean;
}

export interface IUploadedJournalEntryLine extends Dtos.JournalEntryLine {

    classError: boolean;
    accountError: boolean;
    taxCodeError: boolean;
    entityError: boolean;
}

export interface IUploadedExpenseDistribution extends Dtos.ExpenseDistribution {

    classError: boolean;
    accountError: boolean;
    taxCodeError: boolean;
    customerError: boolean;
}

export interface IUploadedSalesReceipt extends Omit<Dtos.SalesReceipt, 'distributions'> {
    distributionView: number[];
    attachmentView: number[];
    distributionErrors: boolean;
    customerError: boolean;
    classError: boolean;
    accountError: boolean;
    amountError: boolean;
    currencyError: boolean;
    paymentMethodError: boolean;
    attachmentsDownloaded: boolean;
    attachmentsImported: boolean;
    attachmentErrors: boolean;
    financialYearError: boolean;
}

export interface IUploadedSalesItemDistribution extends Dtos.SalesItemDistribution {
    classError: boolean;
    accountError: boolean;
    itemError: boolean;
    taxCodeError: boolean;
}

export interface IUploadedDeposit extends Omit<Dtos.Deposit, 'distributions'> {
    distributionView: number[];
    attachmentView: number[];
    distributionErrors: boolean;
    accountError: boolean;
    amountError: boolean;
    currencyError: boolean;
    attachmentsDownloaded: boolean;
    attachmentsImported: boolean;
    attachmentErrors: boolean;
    financialYearError: boolean;
}

export interface IUploadedDepositDistribution extends Dtos.DepositDistribution {
    classError: boolean;
    accountError: boolean;
    paymentMethodError: boolean;
    customerError: boolean;
    taxCodeError: boolean;
}

export interface IUploadedAttachment extends Dtos.Attachment {
    downloadStatus: IRequestState | null;
    importStatus: IRequestState | null;
}


interface IInitialIifImportState {
    fileInformation: Record<string, IifFileInformation> | null;
    files: Record<number, IIifFile> | null;
    bills: Record<number, IUploadedBill> | null;
    expenses: Record<number, IUploadedExpense> | null;
    salesReceipts: Record<number, IUploadedSalesReceipt> | null;
    deposits: Record<number, IUploadedDeposit> | null;
    journalEntries: Record<number, IUploadedJournalEntry> | null;
    journalEntryLines: Record<number, IUploadedJournalEntryLine> | null;
    expenseDistributions: Record<number, IUploadedExpenseDistribution> | null;
    salesItemDistributions: Record<number, IUploadedSalesItemDistribution> | null;
    depositDistributions: Record<number, IUploadedDepositDistribution> | null;


    attachments: Record<number, IUploadedAttachment> | null;

    loadStatus: Record<number, IRequestState> | null;
    importStatus: Record<number, IRequestState> | null;
    view: number[] | null;
    nextIndices: {
        billIndex: number;
        expenseIndex: number;
        salesReceiptIndex: number;
        depositIndex: number;
        journalEntryIndex: number;
        journalEntryLineIndex: number;
        expenseDistributionIndex: number;
        salesItemDistributionIndex: number;
        depositDistributionIndex: number;
        attachmentIndex: number;
    };
}

export interface IIifImportState {
    iifImport: IInitialIifImportState;
}

/*
 * ---------------------------------------------------------------------------------
 * Initial State
 * ---------------------------------------------------------------------------------
 */

const IifImportInitialState: IInitialIifImportState = {
    fileInformation: null,
    files: null,
    bills: null,
    expenses: null,
    salesReceipts: null,
    deposits: null,
    journalEntries: null,
    journalEntryLines: null,
    expenseDistributions: null,
    salesItemDistributions: null,
    depositDistributions: null,

    attachments: null,

    loadStatus: null,
    importStatus: null,
    view: null,
    nextIndices: {
        billIndex: 0,
        expenseIndex: 0,
        salesReceiptIndex: 0,
        depositIndex: 0,
        journalEntryIndex: 0,
        journalEntryLineIndex: 0,
        expenseDistributionIndex: 0,
        salesItemDistributionIndex: 0,
        depositDistributionIndex: 0,
        attachmentIndex: 0
    }
};

/*
* ---------------------------------------------------------------------------------
* Selectors
* ---------------------------------------------------------------------------------
*/

export const useIifImportSelector: TypedUseSelectorHook<IIifImportState> = useSelector;

export const IifImportSelectors = {
    selectFile: (state: IIifImportState, fileIndex: number) => state.iifImport.files && state.iifImport.files[fileIndex] ? state.iifImport.files[fileIndex] : null,
    selectBill: (state: IIifImportState, billIndex: number) => state.iifImport.bills && state.iifImport.bills[billIndex] ? state.iifImport.bills[billIndex] : null,
    selectExpense: (state: IIifImportState, expenseIndex: number) => state.iifImport.expenses && state.iifImport.expenses[expenseIndex] ? state.iifImport.expenses[expenseIndex] : null,
    selectSalesReceipt: (state: IIifImportState, salesReceiptIndex: number) => state.iifImport.salesReceipts && state.iifImport.salesReceipts[salesReceiptIndex] ? state.iifImport.salesReceipts[salesReceiptIndex] : null,
    selectDeposit: (state: IIifImportState, depositIndex: number) => state.iifImport.deposits && state.iifImport.deposits[depositIndex] ? state.iifImport.deposits[depositIndex] : null,
    selectJournalEntry: (state: IIifImportState, journalEntryIndex: number) => state.iifImport.journalEntries && state.iifImport.journalEntries[journalEntryIndex] ? state.iifImport.journalEntries[journalEntryIndex] : null,
    selectJournalEntryLine: (state: IIifImportState, journalEntryLineIndex: number) => state.iifImport.journalEntryLines && state.iifImport.journalEntryLines[journalEntryLineIndex] ? state.iifImport.journalEntryLines[journalEntryLineIndex] : null,
    selectExpenseDistribution: (state: IIifImportState, expenseDistributionIndex: number) => state.iifImport.expenseDistributions && state.iifImport.expenseDistributions[expenseDistributionIndex] ? state.iifImport.expenseDistributions[expenseDistributionIndex] : null,
    selectSalesItemDistribution: (state: IIifImportState, salesItemDistributionIndex: number) => state.iifImport.salesItemDistributions && state.iifImport.salesItemDistributions[salesItemDistributionIndex] ? state.iifImport.salesItemDistributions[salesItemDistributionIndex] : null,
    selectDepositDistribution: (state: IIifImportState, depositDistributionIndex: number) => state.iifImport.depositDistributions && state.iifImport.depositDistributions[depositDistributionIndex] ? state.iifImport.depositDistributions[depositDistributionIndex] : null,

    selectAttachment: (state: IIifImportState, attachmentIndex: number) => state.iifImport.attachments && state.iifImport.attachments[attachmentIndex] ? state.iifImport.attachments[attachmentIndex] : null,

    selectLoadStatus: (state: IIifImportState, fileIndex: number) => state.iifImport.loadStatus && state.iifImport.loadStatus[fileIndex] ? state.iifImport.loadStatus[fileIndex] : { state: RequestState.None },
    selectImportStatus: (state: IIifImportState, fileIndex: number) => state.iifImport.importStatus && state.iifImport.importStatus[fileIndex] ? state.iifImport.importStatus[fileIndex] : { state: RequestState.None },
    selectView: (state: IIifImportState) => state.iifImport.view,
    selectLoadState: (state: IIifImportState) => {
        if (!state.iifImport.loadStatus) {
            return { state: RequestState.None };
        }

        const keys = Object.keys(state.iifImport.loadStatus);

        if (keys.some(key => state.iifImport.loadStatus && state.iifImport.loadStatus[key].state === RequestState.Failure)) {
            return { state: RequestState.Failure };
        }

        if (keys.some(key => state.iifImport.loadStatus && state.iifImport.loadStatus[key].state === RequestState.Pending)) {
            return { state: RequestState.Pending };
        }

        if (keys.some(key => state.iifImport.loadStatus && state.iifImport.loadStatus[key].state === RequestState.Success)) {
            return { state: RequestState.Success };
        }

        return { state: RequestState.None };
    }
};

/*
* ---------------------------------------------------------------------------------
* Reducer
* ---------------------------------------------------------------------------------
*/

class IifImportReducer extends ImmerReducer<IInitialIifImportState> {
    public processFiles(files: FileList) {
        if (!this.draftState.fileInformation) {
            this.draftState.fileInformation = {};
        }

        const fileNames: string[] = [];

        Array.from(files).forEach(file => {
            if (!this.draftState.fileInformation) {
                return;
            }

            fileNames.push(file.name);

            if (this.draftState.fileInformation[file.name]) {
                return;
            }

            this.draftState.fileInformation[file.name] = {
                state: IifFileState.load,
                file
            }
        });

        const removed = Object.keys(this.draftState.fileInformation).filter(x => !fileNames.includes(x));

        removed.forEach(removedName => {
            if (!this.draftState.fileInformation) {
                return;
            }

            this.draftState.fileInformation[removedName].state = IifFileState.remove;
        });
    }

    public refreshFile(filename: string, fileIndex: number) {
        if (!this.draftState.fileInformation) {
            this.draftState.fileInformation = {}
        }

        this.draftState.fileInformation[filename].state = IifFileState.refresh;

        if (!this.draftState.loadStatus) {
            this.draftState.loadStatus = {};
        }

        this.draftState.loadStatus[fileIndex] = {
            state: RequestState.Pending
        }
    }

    public refreshFileSuccess(filename: string, fileIndex: number, file: Dtos.ImportFile) {
        this.removeFile(filename, fileIndex);
        this.uploadFileSuccess(filename, fileIndex, file);
    }

    public refreshFileFailure(filename: string, fileIndex: number, responseStatus?: Dtos.ResponseStatus) {
        this.removeFile(filename, fileIndex);
        this.uploadFileFailure(filename, fileIndex, responseStatus);
    }

    public removeFileAndInformation(filename: string, fileIndex: number) {
        this.removeFile(filename, fileIndex);

        if (!this.draftState.fileInformation) {
            return;
        }

        delete this.draftState.fileInformation[filename];
    }

    public removeFile(filename: string, fileIndex: number) {
        if (this.draftState.importStatus) {
            delete this.draftState.importStatus[fileIndex];
        }

        if (this.draftState.loadStatus) {
            delete this.draftState.loadStatus[fileIndex];
        }

        if (this.draftState.view) {
            const index = this.draftState.view.indexOf(fileIndex);

            this.draftState.view.splice(index, 1);
        }

        if (!this.draftState.files) {
            return;
        }

        const billIndices = this.draftState.files[fileIndex].billView;

        const expenseIndices = this.draftState.files[fileIndex].expenseView;

        let attachmentIndices: number[] = [];

        const expenseDistributionIndices = billIndices
            .map(billIndex => {
                if (!this.draftState.bills || !this.draftState.bills[billIndex]) {
                    return [];
                }

                const distributionIndices = this.draftState.bills[billIndex].distributionView;

                attachmentIndices = attachmentIndices.concat(this.draftState.bills[billIndex].attachmentView);

                delete this.draftState.bills[billIndex];

                return distributionIndices;
            })
            .reduce((a, b) => {
                if (b === null) {
                    return a;
                }

                return a?.concat(b);
            }, [])
            .concat(
                expenseIndices
                    .map(expenseIndex => {
                        if (!this.draftState.expenses || !this.draftState.expenses[expenseIndex]) {
                            return [];
                        }

                        const distributionIndices = this.draftState.expenses[expenseIndex].distributionView;

                        attachmentIndices = attachmentIndices.concat(this.draftState.expenses[expenseIndex].attachmentView);

                        delete this.draftState.expenses[expenseIndex];

                        return distributionIndices;
                    })
                    .reduce((a, b) => {
                        if (b === null) {
                            return a;
                        }

                        return a?.concat(b);
                    }, [])
            );

        expenseDistributionIndices
            .forEach(expenseDistributionIndex => {
                if (!this.draftState.expenseDistributions || !this.draftState.expenseDistributions[expenseDistributionIndex]) {
                    return;
                }

                delete this.draftState.expenseDistributions[expenseDistributionIndex];
            });

        const saleReceiptIndices = this.draftState.files[fileIndex].salesReceiptView;

        const salesItemDistributionIndices = saleReceiptIndices
            .map(saleReceiptIndex => {
                if (!this.draftState.salesReceipts || !this.draftState.salesReceipts[saleReceiptIndex]) {
                    return [];
                }

                const distributionIndices = this.draftState.salesReceipts[saleReceiptIndex].distributionView;

                attachmentIndices = attachmentIndices.concat(this.draftState.salesReceipts[saleReceiptIndex].attachmentView);

                delete this.draftState.salesReceipts[saleReceiptIndex];

                return distributionIndices;
            })
            .reduce((a, b) => {
                if (b === null) {
                    return a;
                }

                return a?.concat(b);
            }, []);

        salesItemDistributionIndices
            .forEach(salesItemDistributionIndex => {
                if (!this.draftState.salesItemDistributions || !this.draftState.salesItemDistributions[salesItemDistributionIndex]) {
                    return;
                }

                delete this.draftState.salesItemDistributions[salesItemDistributionIndex];
            });


        const depositIndices = this.draftState.files[fileIndex].salesReceiptView;

        const depositDistributionIndices = depositIndices
            .map(depositIndex => {
                if (!this.draftState.deposits || !this.draftState.deposits[depositIndex]) {
                    return [];
                }

                const distributionIndices = this.draftState.deposits[depositIndex].distributionView;

                attachmentIndices = attachmentIndices.concat(this.draftState.deposits[depositIndex].attachmentView);

                delete this.draftState.deposits[depositIndex];

                return distributionIndices;
            })
            .reduce((a, b) => {
                if (b === null) {
                    return a;
                }

                return a?.concat(b);
            }, []);

        depositDistributionIndices
            .forEach(depositDistributionIndex => {
                if (!this.draftState.depositDistributions || !this.draftState.depositDistributions[depositDistributionIndex]) {
                    return;
                }

                delete this.draftState.depositDistributions[depositDistributionIndex];
            });

            // --------------------------------

        const journalEntryIndices = this.draftState.files[fileIndex].journalEntryView;

        const journalEntryLineIndices = journalEntryIndices
            .map(journalEntryIndex => {
                if (!this.draftState.journalEntries || !this.draftState.journalEntries[journalEntryIndex]) {
                    return [];
                }

                const lineIndices = this.draftState.journalEntries[journalEntryIndex].lineView;

                attachmentIndices = attachmentIndices.concat(this.draftState.journalEntries[journalEntryIndex].attachmentView);

                delete this.draftState.journalEntries[journalEntryIndex];

                return lineIndices;
            })
            .reduce((a, b) => {
                if (b === null) {
                    return a;
                }

                return a?.concat(b);
            }, [])

        journalEntryLineIndices
            .forEach(journalEntryLineIndex => {
                if (!this.draftState.journalEntryLines || !this.draftState.journalEntryLines[journalEntryLineIndex]) {
                    return;
                }

                delete this.draftState.journalEntryLines[journalEntryLineIndex];
            });
        // ------------
        attachmentIndices
            .forEach(attacmentIndex => {
                if (!this.draftState.attachments || !this.draftState.attachments[attacmentIndex]) {
                    return;
                }

                delete this.draftState.attachments[attacmentIndex];
            });

        delete this.draftState.files[fileIndex];
    }

    public uploadFile(filename: string, fileIndex: number, file: File) {
        if (!this.draftState.loadStatus) {
            this.draftState.loadStatus = {};
        }

        if (!this.draftState.fileInformation) {
            this.draftState.fileInformation = {}
        }

        this.draftState.fileInformation[filename].state = IifFileState.loading;

        this.draftState.loadStatus[fileIndex] = {
            state: RequestState.Pending
        }
    }

    public uploadFileSuccess(filename: string, fileIndex: number, file: Dtos.ImportFile) {
        if (!this.draftState.loadStatus) {
            return;
        }

        if (!this.draftState.files) {
            this.draftState.files = {};
        }

        if (!this.draftState.importStatus) {
            this.draftState.importStatus = {};
        }

        if (!this.draftState.bills) {
            this.draftState.bills = {};
        }

        if (!this.draftState.expenses) {
            this.draftState.expenses = {};
        }

        if (!this.draftState.salesReceipts) {
            this.draftState.salesReceipts = {};
        }

        if (!this.draftState.deposits) {
            this.draftState.deposits = {};
        }

        if (!this.draftState.journalEntries) {
            this.draftState.journalEntries = {};
        }

        if (!this.draftState.journalEntryLines) {
            this.draftState.journalEntryLines = {};
        }

        if (!this.draftState.expenseDistributions) {
            this.draftState.expenseDistributions = {};
        }

        if (!this.draftState.salesItemDistributions) {
            this.draftState.salesItemDistributions = {};
        }

        if (!this.draftState.depositDistributions) {
            this.draftState.depositDistributions = {};
        }

        if (!this.draftState.attachments) {
            this.draftState.attachments = {};
        }

        if (!this.draftState.view) {
            this.draftState.view = [];
        }

        const { bills, expenses, salesReceipts, billAttachments, expenseAttachments, salesReceiptAttachments, deposits, depositAttachments, journalEntries, journalEntryAttachments, ...restFile } = file;

        const billView: number[] = [];
        let billErrors: boolean = false;
        const expenseView: number[] = [];
        let expenseErrors: boolean = false;
        const salesReceiptView: number[] = [];
        let salesReceiptErrors: boolean = false;
        const depositView: number[] = [];
        let depositErrors: boolean = false;
        const journalEntryView: number[] = [];
        let journalEntryErrors: boolean = false;
        let attachmentsDownloaded: boolean = true;
        let financialYearErrors: number = 0;
        const financialYearEnd: string = new Date().getMonth() > 3 /* april */ ? `${new Date().getFullYear()}-04-01` : `${new Date().getFullYear() - 1}-04-01`;

        bills?.forEach((bill, billIndex) => {
            if (!this.draftState.bills) {
                return;
            }

            const { distributions, ...restBill } = bill;

            const distributionView: number[] = [];

            let distributionErrors: boolean = false;

            distributions?.forEach(distribution => {
                if (!this.draftState.expenseDistributions) {
                    return;
                }

                let classError = false;
                let distributionAccountError = false;
                let taxCodeError = false;
                let customerError = false;

                if (!distribution.account?.id) {
                    distributionAccountError = true;
                    distributionErrors = true;
                }

                if (!distribution.class?.id) {
                    classError = true;
                    distributionErrors = true;
                }

                if (!distribution.taxCodeRef?.id) {
                    taxCodeError = true;
                    distributionErrors = true;
                }

                if (distribution.customer && distribution.customer.value && !distribution.customer.id) {
                    customerError = true;
                    distributionErrors = true;
                }

                this.draftState.expenseDistributions[this.draftState.nextIndices.expenseDistributionIndex] = {
                    ...distribution,
                    accountError: distributionAccountError,
                    classError,
                    taxCodeError,
                    customerError
                };


                distributionView.push(this.draftState.nextIndices.expenseDistributionIndex);

                this.draftState.nextIndices.expenseDistributionIndex++;
            });

            const attachmentView: number[] = [];

            billAttachments[billIndex].forEach(billAttachment => {
                if (!this.draftState.attachments) {
                    return;
                }

                this.draftState.attachments[this.draftState.nextIndices.attachmentIndex] = {
                    ...billAttachment,
                    downloadStatus: billAttachment.guid ?
                        {
                            state: RequestState.Success
                        } :
                        {
                            state: RequestState.None
                        },
                    importStatus: null,
                };

                attachmentView.push(this.draftState.nextIndices.attachmentIndex);

                this.draftState.nextIndices.attachmentIndex++;
            })

            let vendorError = false;
            let accountError = false;
            let amountError = false;

            if (bill.distributionTotal !== bill.totalAmount) {
                amountError = true;
                billErrors = true;
            }

            if (!bill.account?.id) {
                accountError = true;
                billErrors = true;
            }

            if (!bill.vendor?.id) {
                vendorError = true;
                billErrors = true;
            }

            let currencyError = false;

            if (!bill.currency?.id) {
                currencyError = true;
                billErrors = true;
            }

            if (distributionErrors) {
                billErrors = true;
            }

            let aDownloaded = true;

            if (attachmentView.length > 0) {
                attachmentsDownloaded = false;
                aDownloaded = false;
            }

            let financialYearError = false;

            if (bill.transactionDate && bill.transactionDate < financialYearEnd) {
                financialYearError = true;
                financialYearErrors++;
            }


            this.draftState.bills[this.draftState.nextIndices.billIndex] = {
                ...restBill,
                distributionView,
                attachmentView,
                distributionErrors,
                vendorError,
                accountError,
                amountError,
                currencyError,
                attachmentsDownloaded: aDownloaded,
                attachmentErrors: false,
                attachmentsImported: aDownloaded,
                financialYearError
            };

            billView.push(this.draftState.nextIndices.billIndex);

            this.draftState.nextIndices.billIndex++;
        });

        expenses?.forEach((expense, expenseIndex) => {
            if (!this.draftState.expenses) {
                return;
            }

            const { distributions, ...restExpense } = expense;

            const distributionView: number[] = [];
            let distributionErrors: boolean = false;

            distributions?.forEach(distribution => {
                if (!this.draftState.expenseDistributions) {
                    return;
                }

                let classError = false;
                let distributionAccountError = false;
                let taxCodeError = false;
                let customerError = false;

                if (!distribution.account?.id) {
                    distributionAccountError = true;
                    distributionErrors = true;
                }

                if (!distribution.class?.id) {
                    classError = true;
                    distributionErrors = true;
                }

                if (!distribution.taxCodeRef?.id) {
                    taxCodeError = true;
                    distributionErrors = true;
                }

                if (distribution.customer && distribution.customer.value && !distribution.customer.id) {
                    customerError = true;
                    distributionErrors = true;
                }

                this.draftState.expenseDistributions[this.draftState.nextIndices.expenseDistributionIndex] = {
                    ...distribution,
                    accountError: distributionAccountError,
                    classError,
                    taxCodeError,
                    customerError
                };

                distributionView.push(this.draftState.nextIndices.expenseDistributionIndex);

                this.draftState.nextIndices.expenseDistributionIndex++;
            });

            const attachmentView: number[] = [];

            expenseAttachments[expenseIndex].forEach(expenseAttachment => {
                if (!this.draftState.attachments) {
                    return;
                }

                this.draftState.attachments[this.draftState.nextIndices.attachmentIndex] = {
                    ...expenseAttachment,
                    downloadStatus: expenseAttachment.guid ?
                        {
                            state: RequestState.Success
                        } :
                        {
                            state: RequestState.None
                        },
                    importStatus: null
                };

                attachmentView.push(this.draftState.nextIndices.attachmentIndex);

                this.draftState.nextIndices.attachmentIndex++;
            })

            let vendorError = false;
            let accountError = false;
            let amountError = false;
            let paymentMethodError = false;

            if (expense.distributionTotal !== expense.totalAmount) {
                amountError = true;
                billErrors = true;
            }

            if (!expense.account?.id) {
                accountError = true;
                expenseErrors = true;
            }

            if (!expense.vendor?.id) {
                vendorError = true;
                expenseErrors = true;
            }

            let currencyError = false;

            if (!expense.currency?.id) {
                currencyError = true;
                expenseErrors = true;
            }

            if (distributionErrors) {
                expenseErrors = true;
            }

            if (expense.paymentMethod && expense.paymentMethod.value && !expense.paymentMethod.id) {
                paymentMethodError = true;
                expenseErrors = true;
            }


            let aDownloaded = true;

            if (attachmentView.length > 0) {
                attachmentsDownloaded = false;
                aDownloaded = false;
            }

            let financialYearError = false;

            if (expense.transactionDate && expense.transactionDate < financialYearEnd) {
                financialYearError = true;
                financialYearErrors++;
            }


            this.draftState.expenses[this.draftState.nextIndices.expenseIndex] = {
                ...restExpense,
                distributionView,
                attachmentView,
                distributionErrors,
                vendorError,
                accountError,
                amountError,
                currencyError,
                paymentMethodError,
                attachmentsDownloaded: aDownloaded,
                attachmentErrors: false,
                attachmentsImported: aDownloaded,
                financialYearError
            };

            expenseView.push(this.draftState.nextIndices.expenseIndex);

            this.draftState.nextIndices.expenseIndex++;
        });

        salesReceipts?.forEach((salesReceipt, salesReceiptIndex) => {
            if (!this.draftState.salesReceipts) {
                return;
            }

            const { distributions, ...restSalesReceipt } = salesReceipt;

            const distributionView: number[] = [];
            let distributionErrors: boolean = false;

            distributions?.forEach(distribution => {
                if (!this.draftState.salesItemDistributions) {
                    return;
                }

                let distributionClassError = false;
                let distributionAccountError = false;
                let taxCodeError = false;
                let itemError = false;

                if (!distribution.itemAccount?.id) {
                    distributionAccountError = true;
                    distributionErrors = true;
                }

                if (!distribution.class?.id) {
                    distributionClassError = true;
                    distributionErrors = true;
                }

                if (!distribution.item?.id) {
                    itemError = true;
                    distributionErrors = true;
                }

                if (!distribution.taxCodeRef?.id) {
                    taxCodeError = true;
                    distributionErrors = true;
                }

                this.draftState.salesItemDistributions[this.draftState.nextIndices.salesItemDistributionIndex] = {
                    ...distribution,
                    accountError: distributionAccountError,
                    classError: distributionClassError,
                    itemError,
                    taxCodeError
                };

                distributionView.push(this.draftState.nextIndices.salesItemDistributionIndex);

                this.draftState.nextIndices.salesItemDistributionIndex++;
            });

            const attachmentView: number[] = [];

            salesReceiptAttachments[salesReceiptIndex].forEach(salesReceiptAttachment => {
                if (!this.draftState.attachments) {
                    return;
                }

                this.draftState.attachments[this.draftState.nextIndices.attachmentIndex] = {
                    ...salesReceiptAttachment,
                    downloadStatus: salesReceiptAttachment.guid ?
                        {
                            state: RequestState.Success
                        } :
                        {
                            state: RequestState.None
                        },
                    importStatus: null
                };

                attachmentView.push(this.draftState.nextIndices.attachmentIndex);

                this.draftState.nextIndices.attachmentIndex++;
            })

            let customerError = false;
            let accountError = false;
            let amountError = false;
            let classError = false;
            let paymentMethodError = false;

            if (salesReceipt.distributionTotal !== salesReceipt.totalAmount) {
                amountError = true;
                salesReceiptErrors = true;
            }

            if (!salesReceipt.depositToAccount?.id) {
                accountError = true;
                salesReceiptErrors = true;
            }

            if (!salesReceipt.customer?.id) {
                customerError = true;
                salesReceiptErrors = true;
            }

            if (!salesReceipt.class?.id) {
                classError = true;
                salesReceiptErrors = true;
            }

            let currencyError = false;

            if (!salesReceipt.currency?.id) {
                currencyError = true;
                salesReceiptErrors = true;
            }

            if (distributionErrors) {
                salesReceiptErrors = true;
            }

            if (salesReceipt.paymentMethod && salesReceipt.paymentMethod.value && !salesReceipt.paymentMethod.id) {
                paymentMethodError = true;
                salesReceiptErrors = true;
            }


            let aDownloaded = true;

            if (attachmentView.length > 0) {
                attachmentsDownloaded = false;
                aDownloaded = false;
            }

            let financialYearError = false;

            if (salesReceipt.transactionDate && salesReceipt.transactionDate < financialYearEnd) {
                financialYearError = true;
                financialYearErrors++;
            }


            this.draftState.salesReceipts[this.draftState.nextIndices.salesReceiptIndex] = {
                ...restSalesReceipt,
                distributionView,
                attachmentView,
                distributionErrors,
                customerError,
                accountError,
                amountError,
                classError,
                currencyError,
                paymentMethodError,
                attachmentsDownloaded: aDownloaded,
                attachmentErrors: false,
                attachmentsImported: aDownloaded,
                financialYearError
            };

            salesReceiptView.push(this.draftState.nextIndices.salesReceiptIndex);

            this.draftState.nextIndices.salesReceiptIndex++;
        });

        deposits?.forEach((deposit, depositIndex) => {
            if (!this.draftState.deposits) {
                return;
            }

            const { distributions, ...restDeposit } = deposit;

            const distributionView: number[] = [];
            let distributionErrors: boolean = false;

            distributions?.forEach(distribution => {
                if (!this.draftState.depositDistributions) {
                    return;
                }

                let distributionClassError = false;
                let distributionAccountError = false;
                let taxCodeError = false;
                const paymentMethodError = false;
                let customerError = false;

                if (!distribution.account?.id) {
                    distributionAccountError = true;
                    distributionErrors = true;
                }

                if (!distribution.class?.id) {
                    distributionClassError = true;
                    distributionErrors = true;
                }

                // if (!distribution.paymentMethod?.id) {
                //    paymentMethodError = true;
                //    distributionErrors = true;
                // }

                if (!distribution.customer?.id) {
                    customerError = true;
                    distributionErrors = true;
                }

                if (!distribution.taxCodeRef?.id) {
                    taxCodeError = true;
                    distributionErrors = true;
                }

                this.draftState.depositDistributions[this.draftState.nextIndices.depositDistributionIndex] = {
                    ...distribution,
                    accountError: distributionAccountError,
                    classError: distributionClassError,
                    paymentMethodError,
                    taxCodeError,
                    customerError
                };

                distributionView.push(this.draftState.nextIndices.depositDistributionIndex);

                this.draftState.nextIndices.depositDistributionIndex++;
            });

            const attachmentView: number[] = [];

            depositAttachments[depositIndex].forEach(depositAttachment => {
                if (!this.draftState.attachments) {
                    return;
                }

                this.draftState.attachments[this.draftState.nextIndices.attachmentIndex] = {
                    ...depositAttachment,
                    downloadStatus: depositAttachment.guid ?
                        {
                            state: RequestState.Success
                        } :
                        {
                            state: RequestState.None
                        },
                    importStatus: null
                };

                attachmentView.push(this.draftState.nextIndices.attachmentIndex);

                this.draftState.nextIndices.attachmentIndex++;
            })

            let accountError = false;
            let amountError = false;

            if (deposit.distributionTotal !== deposit.totalAmount) {
                amountError = true;
                depositErrors = true;
            }

            if (!deposit.depositToAccount?.id) {
                accountError = true;
                depositErrors = true;
            }

            let currencyError = false;

            if (!deposit.currency?.id) {
                currencyError = true;
                depositErrors = true;
            }

            if (distributionErrors) {
                depositErrors = true;
            }


            let aDownloaded = true;

            if (attachmentView.length > 0) {
                attachmentsDownloaded = false;
                aDownloaded = false;
            }

            let financialYearError = false;

            if (deposit.transactionDate && deposit.transactionDate < financialYearEnd) {
                financialYearError = true;
                financialYearErrors++;
            }


            this.draftState.deposits[this.draftState.nextIndices.depositIndex] = {
                ...restDeposit,
                distributionView,
                attachmentView,
                distributionErrors,
                accountError,
                amountError,
                currencyError,
                attachmentsDownloaded: aDownloaded,
                attachmentErrors: false,
                attachmentsImported: aDownloaded,
                financialYearError
            };

            depositView.push(this.draftState.nextIndices.depositIndex);

            this.draftState.nextIndices.depositIndex++;
        });

        // ------------------------------
        journalEntries?.forEach((journalEntry, journalEntryIndex) => {
            if (!this.draftState.journalEntries) {
                return;
            }

            const { lines, ...restBill } = journalEntry;

            const lineView: number[] = [];

            let lineErrors: boolean = false;

            lines?.forEach(journalEntryLine => {
                if (!this.draftState.journalEntryLines) {
                    return;
                }

                let classError = false;
                let lineAccountError = false;
                let taxCodeError = false;
                let entityError = false;

                if (!journalEntryLine.account?.id) {
                    lineAccountError = true;
                    lineErrors = true;
                }

                if (!!journalEntryLine.class && !journalEntryLine.class?.id) {
                    classError = true;
                    lineErrors = true;
                }

                if (!journalEntryLine.taxCodeRef?.id) {
                    taxCodeError = true;
                    lineErrors = true;
                }

                if (!!journalEntryLine.entity && !journalEntryLine.entity.id) {
                    entityError = true;
                    lineErrors = true;
                }

                this.draftState.journalEntryLines[this.draftState.nextIndices.journalEntryLineIndex] = {
                    ...journalEntryLine,
                    accountError: lineAccountError,
                    classError,
                    taxCodeError,
                    entityError
                };


                lineView.push(this.draftState.nextIndices.journalEntryLineIndex);

                this.draftState.nextIndices.journalEntryLineIndex++;
            });

            const attachmentView: number[] = [];

            journalEntryAttachments[journalEntryIndex].forEach(journalAttachment => {
                if (!this.draftState.attachments) {
                    return;
                }

                this.draftState.attachments[this.draftState.nextIndices.attachmentIndex] = {
                    ...journalAttachment,
                    downloadStatus: journalAttachment.guid ?
                        {
                            state: RequestState.Success
                        } :
                        {
                            state: RequestState.None
                        },
                    importStatus: null,
                };

                attachmentView.push(this.draftState.nextIndices.attachmentIndex);

                this.draftState.nextIndices.attachmentIndex++;
            })
            let amountError = false;

            if (journalEntry.lineTotal !== journalEntry.totalAmount || journalEntry.totalAmount !== 0) {
                amountError = true;
                journalEntryErrors = true;
            }

            let currencyError = false;

            if (!journalEntry.currency?.id) {
                currencyError = true;
                journalEntryErrors = true;
            }

            if (lineErrors) {
                journalEntryErrors = true;
            }

            let aDownloaded = true;

            if (attachmentView.length > 0) {
                attachmentsDownloaded = false;
                aDownloaded = false;
            }

            let financialYearError = false;

            if (journalEntry.transactionDate && journalEntry.transactionDate < financialYearEnd) {
                financialYearError = true;
                financialYearErrors++;
            }


            this.draftState.journalEntries[this.draftState.nextIndices.journalEntryIndex] = {
                ...restBill,
                lineView,
                attachmentView,
                lineErrors,
                amountError,
                currencyError,
                attachmentsDownloaded: aDownloaded,
                attachmentErrors: false,
                attachmentsImported: aDownloaded,
                financialYearError
            };

            journalEntryView.push(this.draftState.nextIndices.journalEntryIndex);

            this.draftState.nextIndices.journalEntryIndex++;
        });
        // ------------------------------


        this.draftState.files[fileIndex] = {
            ...restFile,
            billView,
            expenseView,
            salesReceiptView,
            depositView,
            billErrors,
            journalEntryErrors,
            journalEntryView,
            depositErrors,
            expenseErrors,
            salesReceiptErrors,
            attachmentsDownloaded,
            attachmentErrors: false,
            attachmentsImported: attachmentsDownloaded,
            financialYearErrors
        }

        this.draftState.importStatus[fileIndex] = {
            state: RequestState.None
        }

        this.draftState.view?.push(fileIndex);

        this.draftState.view?.sort();

        this.draftState.loadStatus[fileIndex] = {
            state: RequestState.Success
        }

        if (!this.draftState.fileInformation) {
            this.draftState.fileInformation = {}
        }

        this.draftState.fileInformation[filename].state = IifFileState.loaded;
    }

    public uploadFileFailure(filename: string, fileIndex: number, responseStatus?: Dtos.ResponseStatus) {

        if (!this.draftState.files) {
            this.draftState.files = {};
        }

        if (!this.draftState.view) {
            this.draftState.view = [];
        }

        if (!this.draftState.loadStatus) {
            this.draftState.loadStatus = {};
        }

        const newFile = new Dtos.ImportFile({ filename });

        this.draftState.files[fileIndex] = {
            ...newFile,
            billView: [],
            expenseView: [],
            salesReceiptView: [],
            depositView: [],
            journalEntryView: [],
            journalEntryErrors: false,
            billErrors: false,
            expenseErrors: false,
            salesReceiptErrors: false,
            depositErrors: false,
            attachmentErrors: false,
            attachmentsDownloaded: false,
            attachmentsImported: false,
            financialYearErrors: 0
        }

        this.draftState.view?.push(fileIndex);

        this.draftState.view?.sort();

        this.draftState.loadStatus[fileIndex] = {
            state: RequestState.Failure,
            responseStatus
        }

        if (!this.draftState.fileInformation) {
            this.draftState.fileInformation = {}
        }

        this.draftState.fileInformation[filename].state = IifFileState.loaded;
    }

    public import(fileId: number) {
        if (!this.draftState.importStatus) {
            this.draftState.importStatus = {};
        }

        this.draftState.importStatus[fileId] = {
            state: RequestState.Pending
        }
    }

    public importSuccess(fileId: number, billResponses: Dtos.ImportResponse[], expenseResponses: Dtos.ImportResponse[], salesReceiptResponses: Dtos.ImportResponse[], depositResponses: Dtos.ImportResponse[], journalEntryResponses: Dtos.ImportResponse[]) {
        if (!this.draftState.importStatus) {
            this.draftState.importStatus = {};
        }

        if (this.state &&
            this.state.files) {
            this.state
                .files[fileId]
                .billView
                .forEach((billIndex, i) => {
                    if (!billResponses[i].id) {
                        return;
                    }

                    if (!this.draftState.bills) {
                        return;
                    }

                    this.draftState.bills[billIndex].id = billResponses[i].id;
                });

            this.state
                .files[fileId]
                .expenseView
                .forEach((expenseIndex, i) => {
                    if (!expenseResponses[i].id) {
                        return;
                    }

                    if (!this.draftState.expenses) {
                        return;
                    }

                    this.draftState.expenses[expenseIndex].id = expenseResponses[i].id;
                });

            this.state
                .files[fileId]
                .salesReceiptView
                .forEach((salesReceiptIndex, i) => {
                    if (!salesReceiptResponses[i].id) {
                        return;
                    }

                    if (!this.draftState.salesReceipts) {
                        return;
                    }

                    this.draftState.salesReceipts[salesReceiptIndex].id = salesReceiptResponses[i].id;
                });

            this.state
                .files[fileId]
                .depositView
                .forEach((depositIndex, i) => {
                    if (!depositResponses[i].id) {
                        return;
                    }

                    if (!this.draftState.deposits) {
                        return;
                    }

                    this.draftState.deposits[depositIndex].id = depositResponses[i].id;
                });

            this.state
                .files[fileId]
                .journalEntryView
                .forEach((journalEntryIndex, i) => {
                    if (!journalEntryResponses[i].id) {
                        return;
                    }

                    if (!this.draftState.journalEntries) {
                        return;
                    }

                    this.draftState.journalEntries[journalEntryIndex].id = journalEntryResponses[i].id;
                });
        }

        const billFailed = billResponses ? billResponses.map((r, index) => ({ ...r, index })) : [];
        const expenseFailed = expenseResponses ? expenseResponses.map((r, index) => ({ ...r, index })) : [];
        const salesReceiptFailed = salesReceiptResponses ? salesReceiptResponses.map((r, index) => ({ ...r, index })) : [];
        const depositFailed = depositResponses ? depositResponses.map((r, index) => ({ ...r, index })) : [];
        const journalEntryFailed = journalEntryResponses ? journalEntryResponses.map((r, index) => ({ ...r, index })) : [];

        const billFailedCount = billFailed.filter(r => !r.success).length;
        const expenseFailedCount = expenseFailed.filter(r => !r.success).length;
        const salesReceiptFailedCount = salesReceiptFailed.filter(r => !r.success).length;
        const depositFailedCount = depositFailed.filter(r => !r.success).length;
        const journalEntryFailedCount = journalEntryFailed.filter(r => !r.success).length;

        if (billFailedCount > 0 ||
            expenseFailedCount > 0 ||
            salesReceiptFailedCount > 0 ||
            depositFailedCount > 0 ||
            journalEntryFailedCount > 0) {

            const errors: Dtos.ResponseError[] = [];

            const processImportResponse = (type: string, response: Dtos.ImportResponse, index: number): void => {
                if (response.exception && response.exception.innerExceptions && response.exception.innerExceptions.length > 0 && !response.success) {
                    response.exception.innerExceptions.forEach(error => {
                        errors.push(new Dtos.ResponseError({
                            errorCode: error.code,
                            fieldName: `${type} ${(index + 1)}`,
                            message: error.message,
                            meta: {
                                detail: error.detail,
                                position: (index + 1).toString(),
                                type
                            }
                        }))
                    })
                }
            };

            billFailed.forEach(processImportResponse.bind(this, 'Bill'))

            expenseFailed.forEach(processImportResponse.bind(this, 'Expense'))

            salesReceiptFailed.forEach(processImportResponse.bind(this, 'Sales Receipt'))

            depositFailed.forEach(processImportResponse.bind(this, 'Deposit'))

            journalEntryFailed.forEach(processImportResponse.bind(this, 'Journal Entry'))


            this.draftState.importStatus[fileId] = {
                state: RequestState.Failure,
                responseStatus: new Dtos.ResponseStatus({
                    errors
                })
            };

            return;
        }

        this.draftState.importStatus[fileId] = {
            state: RequestState.Success
        }
    }

    public importFailure(fileId: number, responseStatus: Dtos.ResponseStatus) {
        if (!this.draftState.importStatus) {
            this.draftState.importStatus = {};
        }

        this.draftState.importStatus[fileId] = {
            state: RequestState.Failure,
            responseStatus
        }
    }

    public clear() {
        return { ...IifImportInitialState };
    }

    public downloadAttachments(fileIndex: number) {
        return;
    }

    public downloadAttachment(fileIndex: number, type: TransactionType, typeIndex: number, attachmentIndex: number) {
        if (!this.draftState.attachments ||
            !this.draftState.attachments[attachmentIndex]) {
            return;
        }

        this.draftState.attachments[attachmentIndex].downloadStatus = {
            state: RequestState.Pending
        }

        const typeEntries = this.draftState[type];

        if (!typeEntries ||
            !typeEntries[typeIndex]) {
            return;
        }

        typeEntries[typeIndex].attachmentsDownloaded = false;

        if (!this.draftState.files ||
            !this.draftState.files[fileIndex]) {
            return;
        }

        this.draftState.files[fileIndex].attachmentsDownloaded = false;
    }

    public downloadAttachmentSuccess(fileIndex: number, type: TransactionType, typeIndex: number, attachmentIndex: number, attachment: Dtos.Attachment) {
        if (!this.draftState.attachments ||
            !this.draftState.attachments[attachmentIndex]) {
            return;
        }

        this.draftState.attachments[attachmentIndex].guid = attachment.guid;
        this.draftState.attachments[attachmentIndex].type = attachment.type;

        this.draftState.attachments[attachmentIndex].downloadStatus = {
            state: RequestState.Success
        }

        const typeEntries = this.draftState[type];

        if (!typeEntries ||
            !typeEntries[typeIndex]) {
            return;
        }

        typeEntries[typeIndex].attachmentErrors = false;
        typeEntries[typeIndex].attachmentsDownloaded = true;

        typeEntries[typeIndex].attachmentView.forEach(aIndex => {
            if (aIndex === attachmentIndex) {
                return;
            }

            if (!this.state ||
                !this.state.attachments ||
                !this.state.attachments[aIndex]) {
                return;
            }

            if (this.state.attachments[aIndex].downloadStatus?.state === RequestState.Failure) {
                typeEntries[typeIndex].attachmentErrors = true;
            }

            if (this.state.attachments[aIndex].downloadStatus?.state !== RequestState.Success) {
                typeEntries[typeIndex].attachmentsDownloaded = false;
            }

        });

        if (!this.draftState.files ||
            !this.draftState.files[fileIndex]) {
            return;
        }

        this.draftState.files[fileIndex].attachmentErrors = false;
        this.draftState.files[fileIndex].attachmentsDownloaded = true;

        this.draftState.files[fileIndex].billView.forEach(billIndex => {
            if (!this.draftState.files ||
                !this.draftState.files[fileIndex]) {
                return;
            }

            if (type === 'bills' &&
                billIndex === typeIndex) {
                if (typeEntries[typeIndex].attachmentErrors) {
                    this.draftState.files[fileIndex].attachmentErrors = true;
                }

                if (!typeEntries[typeIndex].attachmentsDownloaded) {
                    this.draftState.files[fileIndex].attachmentsDownloaded = false;
                }

                return;
            }

            if (!this.state ||
                !this.state.bills ||
                !this.state.bills[billIndex]) {
                return;
            }


            if (this.state.bills[billIndex].attachmentErrors) {
                this.draftState.files[fileIndex].attachmentErrors = true;
            }

            if (!this.state.bills[billIndex].attachmentsDownloaded) {
                this.draftState.files[fileIndex].attachmentsDownloaded = false;
            }
        });



        this.draftState.files[fileIndex].expenseView.forEach(expenseIndex => {
            if (!this.draftState.files ||
                !this.draftState.files[fileIndex]) {
                return;
            }

            if (type === 'expenses' &&
                expenseIndex === typeIndex) {
                if (typeEntries[typeIndex].attachmentErrors) {
                    this.draftState.files[fileIndex].attachmentErrors = true;
                }

                if (!typeEntries[typeIndex].attachmentsDownloaded) {
                    this.draftState.files[fileIndex].attachmentsDownloaded = false;
                }

                return;
            }

            if (!this.state ||
                !this.state.expenses ||
                !this.state.expenses[expenseIndex]) {
                return;
            }


            if (this.state.expenses[expenseIndex].attachmentErrors) {
                this.draftState.files[fileIndex].attachmentErrors = true;
            }

            if (!this.state.expenses[expenseIndex].attachmentsDownloaded) {
                this.draftState.files[fileIndex].attachmentsDownloaded = false;
            }
        });



        this.draftState.files[fileIndex].salesReceiptView.forEach(salesReceiptIndex => {
            if (!this.draftState.files ||
                !this.draftState.files[fileIndex]) {
                return;
            }

            if (type === 'salesReceipts' &&
                salesReceiptIndex === typeIndex) {
                if (typeEntries[typeIndex].attachmentErrors) {
                    this.draftState.files[fileIndex].attachmentErrors = true;
                }

                if (!typeEntries[typeIndex].attachmentsDownloaded) {
                    this.draftState.files[fileIndex].attachmentsDownloaded = false;
                }

                return;
            }

            if (!this.state ||
                !this.state.salesReceipts ||
                !this.state.salesReceipts[salesReceiptIndex]) {
                return;
            }


            if (this.state.salesReceipts[salesReceiptIndex].attachmentErrors) {
                this.draftState.files[fileIndex].attachmentErrors = true;
            }

            if (!this.state.salesReceipts[salesReceiptIndex].attachmentsDownloaded) {
                this.draftState.files[fileIndex].attachmentsDownloaded = false;
            }
        });

        this.draftState.files[fileIndex].depositView.forEach(depositIndex => {
            if (!this.draftState.files ||
                !this.draftState.files[fileIndex]) {
                return;
            }

            if (type === 'deposits' &&
                depositIndex === typeIndex) {
                if (typeEntries[typeIndex].attachmentErrors) {
                    this.draftState.files[fileIndex].attachmentErrors = true;
                }

                if (!typeEntries[typeIndex].attachmentsDownloaded) {
                    this.draftState.files[fileIndex].attachmentsDownloaded = false;
                }

                return;
            }

            if (!this.state ||
                !this.state.deposits ||
                !this.state.deposits[depositIndex]) {
                return;
            }


            if (this.state.deposits[depositIndex].attachmentErrors) {
                this.draftState.files[fileIndex].attachmentErrors = true;
            }

            if (!this.state.deposits[depositIndex].attachmentsDownloaded) {
                this.draftState.files[fileIndex].attachmentsDownloaded = false;
            }
        });

        // --------------------
        this.draftState.files[fileIndex].journalEntryView.forEach(journalEntryIndex => {
            if (!this.draftState.files ||
                !this.draftState.files[fileIndex]) {
                return;
            }

            if (type === 'journalEntries' &&
                journalEntryIndex === typeIndex) {
                if (typeEntries[typeIndex].attachmentErrors) {
                    this.draftState.files[fileIndex].attachmentErrors = true;
                }

                if (!typeEntries[typeIndex].attachmentsDownloaded) {
                    this.draftState.files[fileIndex].attachmentsDownloaded = false;
                }

                return;
            }

            if (!this.state ||
                !this.state.journalEntries ||
                !this.state.journalEntries[journalEntryIndex]) {
                return;
            }


            if (this.state.journalEntries[journalEntryIndex].attachmentErrors) {
                this.draftState.files[fileIndex].attachmentErrors = true;
            }

            if (!this.state.journalEntries[journalEntryIndex].attachmentsDownloaded) {
                this.draftState.files[fileIndex].attachmentsDownloaded = false;
            }
        });
    }

    public downloadAttachmentFailure(fileIndex: number, type: TransactionType, typeIndex: number, attachmentIndex: number, response?: Dtos.ResponseStatus) {
        if (!this.draftState.attachments ||
            !this.draftState.attachments[attachmentIndex]) {
            return;
        }

        this.draftState.attachments[attachmentIndex].downloadStatus = {
            state: RequestState.Failure,
            responseStatus: response
        }

        const typeEntries = this.draftState[type];

        if (!typeEntries ||
            !typeEntries[typeIndex]) {
            return;
        }

        typeEntries[typeIndex].attachmentsDownloaded = false;
        typeEntries[typeIndex].attachmentErrors = true;

        if (!this.draftState.files ||
            !this.draftState.files[fileIndex]) {
            return;
        }

        this.draftState.files[fileIndex].attachmentsDownloaded = false;
        this.draftState.files[fileIndex].attachmentErrors = true;
    }


    public importAttachments(fileIndex: number) {
        return;
    }

    public importAttachment(fileIndex: number, type: TransactionType, typeIndex: number, attachmentIndex: number) {
        if (!this.draftState.attachments ||
            !this.draftState.attachments[attachmentIndex]) {
            return;
        }

        this.draftState.attachments[attachmentIndex].importStatus = {
            state: RequestState.Pending
        }

        const typeEntries = this.draftState[type];

        if (!typeEntries ||
            !typeEntries[typeIndex]) {
            return;
        }

        typeEntries[typeIndex].attachmentsImported = false;

        if (!this.draftState.files ||
            !this.draftState.files[fileIndex]) {
            return;
        }

        this.draftState.files[fileIndex].attachmentsImported = false;
    }

    public importAttachmentSuccess(fileIndex: number, type: TransactionType, typeIndex: number, attachmentIndex: number, attachment: Dtos.Attachment) {
        if (!this.draftState.attachments ||
            !this.draftState.attachments[attachmentIndex]) {
            return;
        }

        this.draftState.attachments[attachmentIndex].guid = attachment.guid;
        this.draftState.attachments[attachmentIndex].type = attachment.type;

        this.draftState.attachments[attachmentIndex].importStatus = {
            state: RequestState.Success
        }

        const typeEntries = this.draftState[type];

        if (!typeEntries ||
            !typeEntries[typeIndex]) {
            return;
        }

        typeEntries[typeIndex].attachmentErrors = false;
        typeEntries[typeIndex].attachmentsImported = true;

        typeEntries[typeIndex].attachmentView.forEach(aIndex => {
            if (aIndex === attachmentIndex) {
                return;
            }

            if (!this.state ||
                !this.state.attachments ||
                !this.state.attachments[aIndex]) {
                return;
            }

            if (this.state.attachments[aIndex].importStatus?.state === RequestState.Failure) {
                typeEntries[typeIndex].attachmentErrors = true;
            }

            if (this.state.attachments[aIndex].importStatus?.state !== RequestState.Success) {
                typeEntries[typeIndex].attachmentsImported = false;
            }

        });

        if (!this.draftState.files ||
            !this.draftState.files[fileIndex]) {
            return;
        }

        this.draftState.files[fileIndex].attachmentErrors = false;
        this.draftState.files[fileIndex].attachmentsImported = true;

        this.draftState.files[fileIndex].billView.forEach(billIndex => {
            if (!this.draftState.files ||
                !this.draftState.files[fileIndex]) {
                return;
            }

            if (type === 'bills' &&
                billIndex === typeIndex) {
                if (typeEntries[typeIndex].attachmentErrors) {
                    this.draftState.files[fileIndex].attachmentErrors = true;
                }

                if (!typeEntries[typeIndex].attachmentsImported) {
                    this.draftState.files[fileIndex].attachmentsImported = false;
                }

                return;
            }

            if (!this.state ||
                !this.state.bills ||
                !this.state.bills[billIndex]) {
                return;
            }


            if (this.state.bills[billIndex].attachmentErrors) {
                this.draftState.files[fileIndex].attachmentErrors = true;
            }

            if (!this.state.bills[billIndex].attachmentsImported) {
                this.draftState.files[fileIndex].attachmentsImported = false;
            }
        });



        this.draftState.files[fileIndex].expenseView.forEach(expenseIndex => {
            if (!this.draftState.files ||
                !this.draftState.files[fileIndex]) {
                return;
            }

            if (type === 'expenses' &&
                expenseIndex === typeIndex) {
                if (typeEntries[typeIndex].attachmentErrors) {
                    this.draftState.files[fileIndex].attachmentErrors = true;
                }

                if (!typeEntries[typeIndex].attachmentsImported) {
                    this.draftState.files[fileIndex].attachmentsImported = false;
                }

                return;
            }

            if (!this.state ||
                !this.state.expenses ||
                !this.state.expenses[expenseIndex]) {
                return;
            }


            if (this.state.expenses[expenseIndex].attachmentErrors) {
                this.draftState.files[fileIndex].attachmentErrors = true;
            }

            if (!this.state.expenses[expenseIndex].attachmentsImported) {
                this.draftState.files[fileIndex].attachmentsImported = false;
            }
        });



        this.draftState.files[fileIndex].salesReceiptView.forEach(salesReceiptIndex => {
            if (!this.draftState.files ||
                !this.draftState.files[fileIndex]) {
                return;
            }

            if (type === 'salesReceipts' &&
                salesReceiptIndex === typeIndex) {
                if (typeEntries[typeIndex].attachmentErrors) {
                    this.draftState.files[fileIndex].attachmentErrors = true;
                }

                if (!typeEntries[typeIndex].attachmentsImported) {
                    this.draftState.files[fileIndex].attachmentsImported = false;
                }

                return;
            }

            if (!this.state ||
                !this.state.salesReceipts ||
                !this.state.salesReceipts[salesReceiptIndex]) {
                return;
            }


            if (this.state.salesReceipts[salesReceiptIndex].attachmentErrors) {
                this.draftState.files[fileIndex].attachmentErrors = true;
            }

            if (!this.state.salesReceipts[salesReceiptIndex].attachmentsImported) {
                this.draftState.files[fileIndex].attachmentsImported = false;
            }
        });


        this.draftState.files[fileIndex].depositView.forEach(depositIndex => {
            if (!this.draftState.files ||
                !this.draftState.files[fileIndex]) {
                return;
            }

            if (type === 'deposits' &&
                depositIndex === typeIndex) {
                if (typeEntries[typeIndex].attachmentErrors) {
                    this.draftState.files[fileIndex].attachmentErrors = true;
                }

                if (!typeEntries[typeIndex].attachmentsImported) {
                    this.draftState.files[fileIndex].attachmentsImported = false;
                }

                return;
            }

            if (!this.state ||
                !this.state.deposits ||
                !this.state.deposits[depositIndex]) {
                return;
            }


            if (this.state.deposits[depositIndex].attachmentErrors) {
                this.draftState.files[fileIndex].attachmentErrors = true;
            }

            if (!this.state.deposits[depositIndex].attachmentsImported) {
                this.draftState.files[fileIndex].attachmentsImported = false;
            }
        });

        // ---------------------
        this.draftState.files[fileIndex].journalEntryView.forEach(journalEntryIndex => {
            if (!this.draftState.files ||
                !this.draftState.files[fileIndex]) {
                return;
            }

            if (type === 'journalEntries' &&
                journalEntryIndex === typeIndex) {
                if (typeEntries[typeIndex].attachmentErrors) {
                    this.draftState.files[fileIndex].attachmentErrors = true;
                }

                if (!typeEntries[typeIndex].attachmentsImported) {
                    this.draftState.files[fileIndex].attachmentsImported = false;
                }

                return;
            }

            if (!this.state ||
                !this.state.journalEntries ||
                !this.state.journalEntries[journalEntryIndex]) {
                return;
            }


            if (this.state.journalEntries[journalEntryIndex].attachmentErrors) {
                this.draftState.files[fileIndex].attachmentErrors = true;
            }

            if (!this.state.journalEntries[journalEntryIndex].attachmentsImported) {
                this.draftState.files[fileIndex].attachmentsImported = false;
            }
        });
    }

    public importAttachmentFailure(fileIndex: number, type: TransactionType, typeIndex: number, attachmentIndex: number, response?: Dtos.ResponseStatus) {
        if (!this.draftState.attachments ||
            !this.draftState.attachments[attachmentIndex]) {
            return;
        }

        this.draftState.attachments[attachmentIndex].importStatus = {
            state: RequestState.Failure,
            responseStatus: response
        }

        const typeEntries = this.draftState[type];

        if (!typeEntries ||
            !typeEntries[typeIndex]) {
            return;
        }

        typeEntries[typeIndex].attachmentsImported = false;
        typeEntries[typeIndex].attachmentErrors = true;

        if (!this.draftState.files ||
            !this.draftState.files[fileIndex]) {
            return;
        }

        this.draftState.files[fileIndex].attachmentsImported = false;
        this.draftState.files[fileIndex].attachmentErrors = true;
    }
}

export const IifImportActions = createActionCreators(IifImportReducer);
const reducer = createReducerFunction(IifImportReducer, IifImportInitialState);

/*
 * ---------------------------------------------------------------------------------
 * API
 * ---------------------------------------------------------------------------------
 */

export const IifImportApi = {
    uploadFiles: (files: FileList) => {
        const formData = new FormData();

        for (const file of Array.from(files)) {

            formData.append('description', file.name);
            formData.append('type', 'file');
            formData.append('file', file);
        }

        return client.postBody(new Dtos.UploadFile(), formData);
    },
    importFile: (file: Dtos.ImportFile) => {

        return client.post(new Dtos.ImportFileToQuickbooks({ file }));
    },
    uploadAttachment: (attachment: Dtos.Attachment) => {
        return client.post(new Dtos.UploadAttachment({ attachment }));
    },
    importAttachment: (attachment: Dtos.Attachment, type: TransactionType, typeId?: string) => {
        let entityType: string | undefined;

        switch (type) {
            case 'bills':
                entityType = 'Bill';
                break;
            case 'expenses':
                entityType = 'Purchase';
                break;
            case 'salesReceipts':
                entityType = 'SalesReceipt';
                break;
            case 'deposits':
                entityType = 'Deposit';
                break;
            case 'journalEntries':
                entityType = 'JournalEntry';
                break;
        }

        return client.post(new Dtos.ImportAttachment({
            attachment,
            entityType,
            entityId: typeId
        }))
    }
}

/*
 * ---------------------------------------------------------------------------------
 * Logic
 * ---------------------------------------------------------------------------------
 */

const IifImportLogic = {
    uploadFile: createLogic({
        type: IifImportActions.uploadFile.type,
        process: async ({ action }: ILogicSideEffect<IIifImportState, typeof IifImportActions.uploadFile>, dispatch, done) => {
            const [filename, fileIndex, file] = action.payload;

            try {
                const dataTransfer = new DataTransfer();

                dataTransfer.items.add(file);

                const response = await IifImportApi.uploadFiles(dataTransfer.files);

                if (response.files && response.files.length === 1) {
                    dispatch(IifImportActions.uploadFileSuccess(
                        filename,
                        fileIndex,
                        response.files[0]
                    ));
                }
                else {
                    dispatch(IifImportActions.uploadFileFailure(filename, fileIndex, undefined));
                }
            }
            catch (error) {
                dispatch(IifImportActions.uploadFileFailure(filename, fileIndex, error ? error.responseStatus : undefined));
            }

            done();
        }
    }),
    refreshFile: createLogic({
        type: IifImportActions.refreshFile.type,
        latest: true,
        process: async ({ getState, action }: ILogicSideEffect<IIifImportState, typeof IifImportActions.refreshFile>, dispatch, done) => {
            const fileInformation = getState().iifImport.fileInformation;

            const [filename, fileIndex] = action.payload;

            if (!fileInformation ||
                !fileInformation[filename] ||
                fileInformation[filename].state !== IifFileState.refresh) {
                done();
                return;
            }

            try {
                const dataTransfer = new DataTransfer();

                dataTransfer.items.add(fileInformation[filename].file);

                const response = await IifImportApi.uploadFiles(dataTransfer.files);

                if (response.files && response.files.length === 1) {
                    dispatch(IifImportActions.refreshFileSuccess(
                        filename,
                        fileIndex,
                        response.files[0]
                    ));
                }
                else {
                    dispatch(IifImportActions.refreshFileFailure(filename, fileIndex, undefined));
                }
            }
            catch (error) {
                dispatch(IifImportActions.refreshFileFailure(filename, fileIndex, error ? error.responseStatus : undefined));
            }

            done();
        }
    }),
    processFiles: createLogic({
        type: IifImportActions.processFiles.type,
        latest: true,
        process: async ({ getState, action }: ILogicSideEffect<IIifImportState, typeof IifImportActions.processFiles>, dispatch, done) => {
            const fileInformation = getState().iifImport.fileInformation;

            if (!fileInformation) {
                done();
                return;
            }

            const fileInformationKeys = Object.keys(fileInformation);

            const files = Array.from(action.payload);

            fileInformationKeys.forEach((filename, fileIndex) => {
                if (!fileInformation) {
                    return;
                }

                switch (fileInformation[filename].state)
                {
                    case IifFileState.load:
                        dispatch(IifImportActions.uploadFile(
                            filename,
                            fileIndex,
                            fileInformation[filename].file
                        ));
                        break;
                    case IifFileState.refresh:
                        dispatch(IifImportActions.removeFile(
                            filename,
                            fileIndex
                        ));
                        dispatch(IifImportActions.uploadFile(
                            filename,
                            fileIndex,
                            fileInformation[filename].file
                        ));
                        break;
                    case IifFileState.remove:
                        dispatch(IifImportActions.removeFileAndInformation(
                            filename,
                            fileIndex
                        ));
                        break;
                }
            });

            done();
        }
    }),
    importFiles: createLogic({
        type: IifImportActions.import.type,
        latest: true,
        process: async ({ action, getState }: ILogicSideEffect<IIifImportState, typeof IifImportActions.import>, dispatch, done) => {
            try {
                const state = getState();

                const importFile = getFile(state.iifImport, action.payload);

                if (!importFile) {

                    done();
                    return;
                }

                const response = await IifImportApi.importFile(importFile);


                dispatch(IifImportActions.importSuccess(action.payload, response.billImportResponses, response.expenseImportResponses, response.salesReceiptImportResponses, response.depositImportResponses, response.jounralEntryResponses) as any);
            }
            catch (error) {
                dispatch(IifImportActions.importFailure(action.payload, error ? error.responseStatus : undefined));
            }

            done();
        }
    }),
    uploadFileSuccess: createLogic({
        type: IifImportActions.uploadFileSuccess.type,
        latest: true,

        process: async ({ action, getState }: ILogicSideEffect<IIifImportState, typeof IifImportActions.uploadFileSuccess>, dispatch, done) => {
            dispatch(IifImportActions.downloadAttachments(action.payload[1]) as any);
            done();
        }
    }),
    refreshFileSuccess: createLogic({
        type: IifImportActions.refreshFileSuccess.type,
        latest: true,

        process: async ({ action, getState }: ILogicSideEffect<IIifImportState, typeof IifImportActions.refreshFileSuccess>, dispatch, done) => {
            dispatch(IifImportActions.downloadAttachments(action.payload[1]) as any);
            done();
        }
    }),
    downloadAttachments: createLogic({
        type: IifImportActions.downloadAttachments.type,
        latest: true,
        process: async ({ action, getState }: ILogicSideEffect<IIifImportState, typeof IifImportActions.downloadAttachments>, dispatch, done) => {
            try {
                const state = getState();

                const attachments = getAttachmentAndMeta(state.iifImport, action.payload);

                if (!attachments) {

                    done();
                    return;
                }

                for (const attachment of attachments) {
                    dispatch(IifImportActions.downloadAttachment(attachment.fileIndex, attachment.type, attachment.typeIndex, attachment.attachmentIndex));
                }

            }
            catch (error) {
                console.log(error)
            }

            done();
        }
    }),
    downloadAttachment: createLogic({
        type: IifImportActions.downloadAttachment.type,
        process: async ({ action, getState }: ILogicSideEffect<IIifImportState, typeof IifImportActions.downloadAttachment>, dispatch, done) => {
            const [fileIndex, type, typeIndex, attachmentIndex] = action.payload;

            try {
                const state = getState();

                const attachment = IifImportSelectors.selectAttachment(state, attachmentIndex);

                if (attachment) {
                    const response = await IifImportApi.uploadAttachment(attachment);

                    dispatch(IifImportActions.downloadAttachmentSuccess(fileIndex, type, typeIndex, attachmentIndex, response.attachment));
                }
            }
            catch (error) {
                dispatch(IifImportActions.downloadAttachmentFailure(fileIndex, type, typeIndex, attachmentIndex, error ? error.responseStatus : undefined));
            }

            done();
        }
    }),
    importFileSuccess: createLogic({
        type: IifImportActions.importSuccess.type,
        latest: true,

        process: async ({ action, getState }: ILogicSideEffect<IIifImportState, typeof IifImportActions.importSuccess>, dispatch, done) => {
            dispatch(IifImportActions.importAttachments(action.payload[0]) as any);
            done();
        }
    }),
    importAttachments: createLogic({
        type: IifImportActions.importAttachments.type,
        latest: true,
        process: async ({ action, getState }: ILogicSideEffect<IIifImportState, typeof IifImportActions.importAttachments>, dispatch, done) => {
            try {
                const state = getState();

                const attachments = getAttachmentAndMeta(state.iifImport, action.payload);

                if (!attachments) {

                    done();
                    return;
                }

                for (const attachment of attachments) {
                    dispatch(IifImportActions.importAttachment(attachment.fileIndex, attachment.type, attachment.typeIndex, attachment.attachmentIndex));
                }

            }
            catch (error) {
                console.log(error)
            }

            done();
        }
    }),
    importAttachment: createLogic({
        type: IifImportActions.importAttachment.type,
        process: async ({ action, getState }: ILogicSideEffect<IIifImportState, typeof IifImportActions.importAttachment>, dispatch, done) => {
            const [fileIndex, type, typeIndex, attachmentIndex] = action.payload;

            try {
                const state = getState();

                const attachment = IifImportSelectors.selectAttachment(state, attachmentIndex);

                if (attachment) {
                    const entities = state.iifImport[type];
                    const entity = entities ? entities[typeIndex] : null;
                    const entityId = entity?.id;

                    const response = await IifImportApi.importAttachment(attachment, type, entityId);

                    dispatch(IifImportActions.importAttachmentSuccess(fileIndex, type, typeIndex, attachmentIndex, response.attachment));
                }
            }
            catch (error) {
                dispatch(IifImportActions.importAttachmentFailure(fileIndex, type, typeIndex, attachmentIndex, error ? error.responseStatus : undefined));
            }

            done();
        }
    })
};

const logics = [
    IifImportLogic.uploadFile,
    IifImportLogic.processFiles,
    IifImportLogic.importFiles,
    IifImportLogic.refreshFile,
    IifImportLogic.uploadFileSuccess,
    IifImportLogic.refreshFileSuccess,
    IifImportLogic.downloadAttachments,
    IifImportLogic.downloadAttachment,
    IifImportLogic.importFileSuccess,
    IifImportLogic.importAttachments,
    IifImportLogic.importAttachment
]



/*
 * ---------------------------------------------------------------------------------
 * Helper Functions
 * ---------------------------------------------------------------------------------
 */

const getFile = (state: IInitialIifImportState, fileIndex: number) => {
    if (!state.files) {
        return null;
    }

    const { billErrors, expenseErrors, billView, expenseView, salesReceiptView, salesReceiptErrors, depositView, depositErrors, journalEntryView, journalEntryErrors, ...file } = state.files[fileIndex];

    const importFile: Dtos.ImportFile = {
        ...file,
        expenses: getExpenses(state, expenseView),
        bills: getBills(state, billView),
        salesReceipts: getSalesReceipts(state, salesReceiptView),
        deposits: getDeposits(state, depositView),
        journalEntries: getJournalEntries(state, journalEntryView),

        billAttachments: getBillAttachments(state, billView),
        expenseAttachments: getExpenseAttachments(state, expenseView),
        salesReceiptAttachments: getSalesReceiptAttachments(state, salesReceiptView),
        depositAttachments: getDepositAttachments(state, depositView),
        journalEntryAttachments: getJournalEntryAttachments(state, journalEntryView),
    };

    return importFile;
}

const getBills = (state: IInitialIifImportState, billView: number[]) => {

    const bills: Dtos.Bill[] = [];

    billView.forEach(billIndex => {
        if (!state.bills) {
            return;
        }

        const { accountError, vendorError, distributionErrors, distributionView, amountError, ...bill } = state.bills[billIndex];

        const importBill: Dtos.Bill = {
            ...bill,
            distributions: getExpenseDistributions(state, distributionView)
        };

        bills.push(importBill);
    });

    return bills;
}

const getJournalEntries = (state: IInitialIifImportState, journalEntryView: number[]) => {

    const journalEntries: Dtos.JournalEntry[] = [];

    journalEntryView.forEach(journalEntryIndex => {
        if (!state.journalEntries) {
            return;
        }

        const { lineErrors, currencyError, amountError, lineView, ...journalEntry } = state.journalEntries[journalEntryIndex];

        const importJournalEntry: Dtos.JournalEntry = {
            ...journalEntry,
            lines: getJournalEntryLines(state, lineView)
        };

        journalEntries.push(importJournalEntry);
    });

    return journalEntries;
}

const getExpenses = (state: IInitialIifImportState, expenseView: number[]) => {

    const expenses: Dtos.Expense[] = [];

    expenseView.forEach(expenseIndex => {
        if (!state.expenses) {
            return;
        }

        const { accountError, vendorError, distributionErrors, distributionView, amountError, ...expense } = state.expenses[expenseIndex];

        const importExpense: Dtos.Expense = {
            ...expense,
            distributions: getExpenseDistributions(state, distributionView)
        };

        expenses.push(importExpense);
    });

    return expenses;
}

const getSalesReceipts = (state: IInitialIifImportState, salesReceiptView: number[]) => {

    const salesReceipts: Dtos.SalesReceipt[] = [];

    salesReceiptView.forEach(expenseIndex => {
        if (!state.salesReceipts) {
            return;
        }

        const { accountError, customerError, classError, distributionErrors, distributionView, amountError, ...expense } = state.salesReceipts[expenseIndex];

        const importSalesReceipt: Dtos.SalesReceipt = {
            ...expense,
            distributions: getSalesItemDistributions(state, distributionView)
        };

        salesReceipts.push(importSalesReceipt);
    });

    return salesReceipts;
}

const getDeposits = (state: IInitialIifImportState, depositView: number[]) => {

    const deposits: Dtos.Deposit[] = [];

    depositView.forEach(expenseIndex => {
        if (!state.deposits) {
            return;
        }

        const { accountError, attachmentErrors, currencyError, distributionErrors, distributionView, amountError, ...expense } = state.deposits[expenseIndex];

        const importDeposit: Dtos.Deposit = {
            ...expense,
            distributions: getDepositDistributions(state, distributionView)
        };

        deposits.push(importDeposit);
    });

    return deposits;
}

const getExpenseDistributions = (state: IInitialIifImportState, distributionView: number[]) => {
    const distributions: Dtos.ExpenseDistribution[] = [];

    distributionView.forEach(distributionIndex => {
        if (!state.expenseDistributions) {
            return;
        }

        const { accountError: distributionAccountError, taxCodeError, classError, ...distribution } = state.expenseDistributions[distributionIndex];

        const importDistribution: Dtos.ExpenseDistribution = {
            ...distribution
        };

        distributions.push(importDistribution);
    });

    return distributions
}

const getJournalEntryLines = (state: IInitialIifImportState, lineView: number[]) => {
    const journalEntryLines: Dtos.JournalEntryLine[] = [];

    lineView.forEach(journalEntryLineIndex => {
        if (!state.journalEntryLines) {
            return;
        }

        const { accountError, taxCodeError, classError, ...line } = state.journalEntryLines[journalEntryLineIndex];

        const importJournalEntryLine: Dtos.JournalEntryLine = {
            ...line
        };

        journalEntryLines.push(importJournalEntryLine);
    });

    return journalEntryLines
}

const getSalesItemDistributions = (state: IInitialIifImportState, distributionView: number[]) => {
    const distributions: Dtos.SalesItemDistribution[] = [];

    distributionView.forEach(distributionIndex => {
        if (!state.salesItemDistributions) {
            return;
        }

        const { accountError: distributionAccountError, taxCodeError, classError, itemError, ...distribution } = state.salesItemDistributions[distributionIndex];

        const importDistribution: Dtos.SalesItemDistribution = {
            ...distribution
        };

        distributions.push(importDistribution);
    });

    return distributions
}

const getDepositDistributions = (state: IInitialIifImportState, distributionView: number[]) => {
    const distributions: Dtos.DepositDistribution[] = [];

    distributionView.forEach(distributionIndex => {
        if (!state.depositDistributions) {
            return;
        }

        const { accountError: distributionAccountError, taxCodeError, classError, paymentMethodError, ...distribution } = state.depositDistributions[distributionIndex];

        const importDistribution: Dtos.DepositDistribution = {
            ...distribution
        };

        distributions.push(importDistribution);
    });

    return distributions
}


const getBillAttachments = (state: IInitialIifImportState, billView: number[]) => {

    const billAttachments: Dtos.Attachment[][] = [];

    billView.forEach(billIndex => {
        if (!state.bills) {
            return;
        }

        const attachments: Dtos.Attachment[] = [];

        const { attachmentView } = state.bills[billIndex];

        attachmentView.forEach((attachmentIndex) => {
            if (!state.attachments) {
                return;
            }

            attachments.push(state.attachments[attachmentIndex]);
        })

        billAttachments.push(attachments);
    });

    return billAttachments;
}


const getJournalEntryAttachments = (state: IInitialIifImportState, journalEntryView: number[]) => {

    const journalEntryAttachments: Dtos.Attachment[][] = [];

    journalEntryView.forEach(journalEntryIndex => {
        if (!state.journalEntries) {
            return;
        }

        const attachments: Dtos.Attachment[] = [];

        const { attachmentView } = state.journalEntries[journalEntryIndex];

        attachmentView.forEach((attachmentIndex) => {
            if (!state.attachments) {
                return;
            }

            attachments.push(state.attachments[attachmentIndex]);
        })

        journalEntryAttachments.push(attachments);
    });

    return journalEntryAttachments;
}

const getExpenseAttachments = (state: IInitialIifImportState, expenseView: number[]) => {

    const expenseAttachments: Dtos.Attachment[][] = [];

    expenseView.forEach(expenseIndex => {
        if (!state.expenses) {
            return;
        }

        const attachments: Dtos.Attachment[] = [];

        const { attachmentView } = state.expenses[expenseIndex];

        attachmentView.forEach((attachmentIndex) => {
            if (!state.attachments) {
                return;
            }

            attachments.push(state.attachments[attachmentIndex]);
        })

        expenseAttachments.push(attachments);
    });

    return expenseAttachments;
}

const getSalesReceiptAttachments = (state: IInitialIifImportState, salesReceiptView: number[]) => {

    const salesReceiptAttachments: Dtos.Attachment[][] = [];

    salesReceiptView.forEach(salesReceiptIndex => {
        if (!state.salesReceipts) {
            return;
        }

        const attachments: Dtos.Attachment[] = [];

        const { attachmentView } = state.salesReceipts[salesReceiptIndex];

        attachmentView.forEach((attachmentIndex) => {
            if (!state.attachments) {
                return;
            }

            attachments.push(state.attachments[attachmentIndex]);
        })

        salesReceiptAttachments.push(attachments);
    });

    return salesReceiptAttachments;
}

const getDepositAttachments = (state: IInitialIifImportState, depositView: number[]) => {

    const depositAttachments: Dtos.Attachment[][] = [];

    depositView.forEach(depositIndex => {
        if (!state.deposits) {
            return;
        }

        const attachments: Dtos.Attachment[] = [];

        const { attachmentView } = state.deposits[depositIndex];

        attachmentView.forEach((attachmentIndex) => {
            if (!state.attachments) {
                return;
            }

            attachments.push(state.attachments[attachmentIndex]);
        })

        depositAttachments.push(attachments);
    });

    return depositAttachments;
}

const getAttachments = (state: IInitialIifImportState, fileIndex: number) => {
    if (!state.files ||
        !state.files[fileIndex] ||
        state.files[fileIndex].billErrors ||
        state.files[fileIndex].expenseErrors ||
        state.files[fileIndex].salesReceiptErrors) {
        return;
    }


    const attachments = state.files[fileIndex].billView
        .reduce<IUploadedAttachment[]>((attachmentCollection, billIndex) => {
            if (!state.bills ||
                !state.bills[billIndex] ||
                !state.bills[billIndex].attachmentView) {
                return attachmentCollection;
            }

            const billAttachments: IUploadedAttachment[] = state.bills[billIndex].attachmentView
                .map(attachmentIndex => {
                    if (!state.attachments ||
                        !state.attachments[attachmentIndex]) {
                        return undefined;
                    }

                    return state.attachments[attachmentIndex];
                })
                .filter(attachment => !!attachment) as unknown as IUploadedAttachment[];

            return attachmentCollection.concat(billAttachments);
        }, [])
        .concat(
            state.files[fileIndex].expenseView
                .reduce<IUploadedAttachment[]>((attachmentCollection, expenseIndex) => {
                    if (!state.expenses ||
                        !state.expenses[expenseIndex] ||
                        !state.expenses[expenseIndex].attachmentView) {
                        return attachmentCollection;
                    }

                    const expenseAttachments: IUploadedAttachment[] = state.expenses[expenseIndex].attachmentView
                        .map(attachmentIndex => {
                            if (!state.attachments ||
                                !state.attachments[attachmentIndex]) {
                                return undefined;
                            }

                            return state.attachments[attachmentIndex];
                        })
                        .filter(attachment => !!attachment) as unknown as IUploadedAttachment[];

                    return attachmentCollection.concat(expenseAttachments);
                }, [])
        )
        .concat(
            state.files[fileIndex].salesReceiptView
                .reduce<IUploadedAttachment[]>((attachmentCollection, salesReceiptIndex) => {
                    if (!state.salesReceipts ||
                        !state.salesReceipts[salesReceiptIndex] ||
                        !state.salesReceipts[salesReceiptIndex].attachmentView) {
                        return attachmentCollection;
                    }

                    const salesReceiptAttachments: IUploadedAttachment[] = state.salesReceipts[salesReceiptIndex].attachmentView
                        .map(attachmentIndex => {
                            if (!state.attachments ||
                                !state.attachments[attachmentIndex]) {
                                return undefined;
                            }

                            return state.attachments[attachmentIndex];
                        })
                        .filter(attachment => !!attachment) as unknown as IUploadedAttachment[];

                    return attachmentCollection.concat(salesReceiptAttachments);
                }, [])
        )
        .concat(
            state.files[fileIndex].depositView
                .reduce<IUploadedAttachment[]>((attachmentCollection, depositIndex) => {
                    if (!state.deposits ||
                        !state.deposits[depositIndex] ||
                        !state.deposits[depositIndex].attachmentView) {
                        return attachmentCollection;
                    }

                    const depositAttachments: IUploadedAttachment[] = state.deposits[depositIndex].attachmentView
                        .map(attachmentIndex => {
                            if (!state.attachments ||
                                !state.attachments[attachmentIndex]) {
                                return undefined;
                            }

                            return state.attachments[attachmentIndex];
                        })
                        .filter(attachment => !!attachment) as unknown as IUploadedAttachment[];

                    return attachmentCollection.concat(depositAttachments);
                }, [])
        );

    return attachments;
};


const getAttachmentAndMeta = (state: IInitialIifImportState, fileIndex: number) => {
    if (!state.files ||
        !state.files[fileIndex] ||
        state.files[fileIndex].billErrors ||
        state.files[fileIndex].expenseErrors ||
        state.files[fileIndex].salesReceiptErrors) {
        return;
    }


    const attachments = state.files[fileIndex].billView
        .reduce<AttachmentAndMeta[]>((attachmentCollection, billIndex) => {
            if (!state.bills ||
                !state.bills[billIndex] ||
                !state.bills[billIndex].attachmentView) {
                return attachmentCollection;
            }

            const billAttachments: AttachmentAndMeta[] = state.bills[billIndex].attachmentView
                .map(attachmentIndex => {
                    if (!state.attachments ||
                        !state.attachments[attachmentIndex]) {
                        return undefined;
                    }

                    return {
                        fileIndex,
                        type: 'bills',
                        typeIndex: billIndex,
                        attachmentIndex,
                        attachment: state.attachments[attachmentIndex]
                    };
                })
                .filter(attachment => !!attachment) as unknown as AttachmentAndMeta[];

            return attachmentCollection.concat(billAttachments);
        }, [])
        .concat(
            state.files[fileIndex].expenseView
                .reduce<AttachmentAndMeta[]>((attachmentCollection, expenseIndex) => {
                    if (!state.expenses ||
                        !state.expenses[expenseIndex] ||
                        !state.expenses[expenseIndex].attachmentView) {
                        return attachmentCollection;
                    }

                    const expenseAttachments: AttachmentAndMeta[] = state.expenses[expenseIndex].attachmentView
                        .map(attachmentIndex => {
                            if (!state.attachments ||
                                !state.attachments[attachmentIndex]) {
                                return undefined;
                            }

                            return {
                                fileIndex,
                                type: 'expenses',
                                typeIndex: expenseIndex,
                                attachmentIndex,
                                attachment: state.attachments[attachmentIndex]
                            };
                        })
                        .filter(attachment => !!attachment) as unknown as AttachmentAndMeta[];

                    return attachmentCollection.concat(expenseAttachments);
                }, [])
        )
        .concat(
            state.files[fileIndex].salesReceiptView
                .reduce<AttachmentAndMeta[]>((attachmentCollection, salesReceiptIndex) => {
                    if (!state.salesReceipts ||
                        !state.salesReceipts[salesReceiptIndex] ||
                        !state.salesReceipts[salesReceiptIndex].attachmentView) {
                        return attachmentCollection;
                    }

                    const salesReceiptAttachments: AttachmentAndMeta[] = state.salesReceipts[salesReceiptIndex].attachmentView
                        .map(attachmentIndex => {
                            if (!state.attachments ||
                                !state.attachments[attachmentIndex]) {
                                return undefined;
                            }

                            return {
                                fileIndex,
                                type: 'salesReceipts',
                                typeIndex: salesReceiptIndex,
                                attachmentIndex,
                                attachment: state.attachments[attachmentIndex]
                            };
                        })
                        .filter(attachment => !!attachment) as unknown as AttachmentAndMeta[];

                    return attachmentCollection.concat(salesReceiptAttachments);
                }, [])
        )
        .concat(
            state.files[fileIndex].depositView
                .reduce<AttachmentAndMeta[]>((attachmentCollection, depositIndex) => {
                    if (!state.deposits ||
                        !state.deposits[depositIndex] ||
                        !state.deposits[depositIndex].attachmentView) {
                        return attachmentCollection;
                    }

                    const depositAttachments: AttachmentAndMeta[] = state.deposits[depositIndex].attachmentView
                        .map(attachmentIndex => {
                            if (!state.attachments ||
                                !state.attachments[attachmentIndex]) {
                                return undefined;
                            }

                            return {
                                fileIndex,
                                type: 'deposits',
                                typeIndex: depositIndex,
                                attachmentIndex,
                                attachment: state.attachments[attachmentIndex]
                            };
                        })
                        .filter(attachment => !!attachment) as unknown as AttachmentAndMeta[];

                    return attachmentCollection.concat(depositAttachments);
                }, [])
        );

    return attachments;
};

const downloadUrl = async (attachmentIndex: number, attachment: IUploadedAttachment, dispatch: Dispatch) => {
    const response = await IifImportApi.uploadAttachment(attachment);
}

/*
 * ---------------------------------------------------------------------------------
 * Register
 * ---------------------------------------------------------------------------------
 */

reducerRegistry.register('iifImport', reducer, logics);


// WEBPACK FOOTER //
// ./src/store/modules/iifImport.ts